/*
 * Prüfbericht 0.63, L01: Label- und Zuständigenänderungen je Karte geordnet senden.
 * - Jede Änderung ist ein Delta ({ add, remove }); der Server wendet es auf seinen aktuellen Stand an.
 * - Je Karte läuft immer nur eine Anfrage; die nächste startet, wenn die vorige fertig ist (auch nach Fehler).
 * - Angezeigt wird: bestätigter Serverstand + noch offene eigene Änderungen. Eine ältere Antwort oder ein
 *   Echtzeit-Ereignis setzt einen neueren Klick deshalb nicht zurück.
 */
export type IdKind = 'labels' | 'assignees';
interface Op {
  seq: number;
  kind: IdKind;
  add: string[];
  remove: string[];
}

const pending = new Map<string, Op[]>();
const chains = new Map<string, Promise<unknown>>();
let seq = 0;

function fold(ids: string[], ops: Op[]): string[] {
  let out = [...ids];
  for (const op of ops) out = [...new Set([...out, ...op.add])].filter((x) => !op.remove.includes(x) || op.add.includes(x));
  return out;
}

/** Offene eigene Änderungen über einen (Server-)Stand legen */
export function withPendingIds<T extends { labelIds: string[]; assigneeIds: string[] }>(cardId: string, card: T): T {
  const ops = pending.get(cardId);
  if (!ops?.length) return card;
  const labels = ops.filter((o) => o.kind === 'labels');
  const people = ops.filter((o) => o.kind === 'assignees');
  return {
    ...card,
    ...(labels.length ? { labelIds: fold(card.labelIds, labels) } : {}),
    ...(people.length ? { assigneeIds: fold(card.assigneeIds, people) } : {}),
  };
}

/** Ergebnis aller offenen Änderungen auf eine Liste anwenden (für die sofortige Anzeige) */
export function applyIdChange(ids: string[], change: { add: string[]; remove: string[] }): string[] {
  return fold(ids, [{ seq: 0, kind: 'labels', ...change }]);
}

export const hasPendingIds = (cardId: string) => !!pending.get(cardId)?.length;

/**
 * Änderung einreihen: sofort als offen vermerken (Anzeige), dann nach allen vorherigen Änderungen dieser Karte
 * senden. Der Rückgabewert ist das Ergebnis von `send` (oder dessen Fehler – er betrifft nur diese Änderung).
 */
export function queueIdChange<T>(cardId: string, kind: IdKind, change: { add: string[]; remove: string[] }, send: () => Promise<T>): Promise<T> {
  const op: Op = { seq: ++seq, kind, add: change.add, remove: change.remove };
  pending.set(cardId, [...(pending.get(cardId) ?? []), op]);
  const prev = chains.get(cardId) ?? Promise.resolve();
  const run = prev
    .catch(() => undefined)
    .then(send)
    .finally(() => {
      const left = (pending.get(cardId) ?? []).filter((o) => o !== op);
      if (left.length) pending.set(cardId, left);
      else pending.delete(cardId);
      if (chains.get(cardId) === run) chains.delete(cardId);
    });
  chains.set(cardId, run);
  return run;
}
